import { AfterViewInit, Directive, ElementRef, HostListener, Injector } from "@angular/core";
import { EventManager } from "@angular/platform-browser";

import { Angulartics2GoogleAnalytics } from "angulartics2";
import { debug } from "@helpers/debug";
import { ModalService } from "@core-services/modal.service";
import { TranslationService } from "@core-services/translation.service";
import { Cryptocoin } from "@models";

@Directive()
export abstract class CustomModal implements AfterViewInit {
	@HostListener("window:keydown", ["$event"])
	public onKeydown(event: KeyboardEvent): void {
		this.onWindowKeyEvent(event);
	}

	// Keyboard events
	private static KEY_TAB: string = "Tab";
	private static KEY_ENT: string = "Enter";
	private static KEY_ESC: string = "Escape";

	// Autofocus + prevent tabbing to outside the modal
	private focusableEls: HTMLElement[] = [];
	private firstFocusableEl: HTMLElement = null;
	private lastFocusableEl: HTMLElement = null;

	// Injections
	public static injector: Injector;
	private elementRef: ElementRef;
	private eventManager: EventManager;
	protected modalService: ModalService;
	protected translationService: TranslationService;

	// Modal
	public data = null;
	public selectedCryptocoin: Cryptocoin = null;

	public constructor() {
		this.elementRef = CustomModal.injector.get(ElementRef);
		this.eventManager = CustomModal.injector.get(EventManager);
		this.modalService = CustomModal.injector.get(ModalService);
		this.translationService = CustomModal.injector.get(TranslationService);
	}

	public ngAfterViewInit(): void {
		this.doTrapFocusTime();
	}

	// TODO: eslint fix
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public close(data: any = null, role: string = ""): void {
		this.modalService.popModalStack(this, data, role);
	}

	public closeAll(close: boolean = true): void {
		this.modalService.popAll();
	}

	public doTrapFocusTime(delay: number = 0): void {
		setTimeout(() => {
			this.doTrapFocus();
		}, delay);
	}

	public addNewFocusableElement(el: HTMLElement): void {
		const elementFound = this.focusableEls.includes(el);

		this.focusableEls = [...this.focusableEls, ...(elementFound ? [] : [el])];
		this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1];
	}

	private doTrapFocus(): void {
		if (this.elementRef) {
			const modalCollection = this.elementRef.nativeElement.querySelector("modal-collection");
			const modalContainer = modalCollection.querySelector(
				`.bp-modal-container > ng-component:not([hidden]),
			 	.bp-modal-container > app-contact-view:not([hidden]),
				.bp-modal-container > * > success-screen:not([hidden]),
				.bp-modal-container > wallet-selection-modal:not([hidden])`
			);

			const focusableEls = modalContainer
				? modalContainer.querySelectorAll(`a[href], area[href], input:not([disabled]):not([type='checkbox']),
			select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]:not([disabled])`)
				: {};
			// Because we are using customized switch on top of checkbox >>> :not([type='checkbox'])
			this.focusableEls = Array.prototype.slice.call(focusableEls);
			if (debug) {
				console.log("[MODALFOCUS]", this.focusableEls);
			}
			if (this.focusableEls.length > 0) {
				this.firstFocusableEl = this.focusableEls[0];
				this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1];
				this.firstFocusableEl.focus();
			} else {
				this.focusableEls = [];
				this.firstFocusableEl = null;
				this.lastFocusableEl = null;
				modalCollection.focus();
			}
		}
	}

	private handleForwardTab(e: KeyboardEvent): void {
		if (document.activeElement === this.lastFocusableEl && this.firstFocusableEl) {
			e.preventDefault();
			this.firstFocusableEl.focus();
		}
	}

	private handleBackwardTab(e: KeyboardEvent): void {
		if (document.activeElement === this.firstFocusableEl && this.lastFocusableEl) {
			e.preventDefault();
			this.lastFocusableEl.focus();
		}
	}

	private onWindowKeyEvent: (event: KeyboardEvent) => void = (e: KeyboardEvent) => {
		// No active modal opened or event triggered from another stacked modal
		if (
			this.modalService.getCurrentModal() == null ||
			this.modalService.getCurrentModal().component.name !== this.constructor.name
		) {
			return;
		}
		switch (e.code) {
			case CustomModal.KEY_ESC:
				if (this.modalService.getCurrentModal().options.preventEscDismiss) {
					return;
				}
				this.close();
				break;
			case CustomModal.KEY_ENT:
				break;
			case CustomModal.KEY_TAB:
				if (this.focusableEls.length <= 1) {
					e.preventDefault();
				} else if (e.shiftKey) {
					this.handleBackwardTab(e);
				} else {
					this.handleForwardTab(e);
				}
				break;
			default:
				break;
		}
	};

	public trackGA(action: string, properties: Object = { category: "" }): void {
		properties["label"] = "broker web"; // Force value for all trackings
		CustomModal.injector.get(Angulartics2GoogleAnalytics).eventTrack(action, properties);
		if (debug) {
			console.log("[GA_TRACK]", "Action:", action, properties);
		}
	}
}
